import { useState, useEffect } from "react";
import { AgentLayout } from "./AgentDashboard";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { FileText, Image, Presentation, Download, Sparkles, Tag, Clock, Palette } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPES = [
  { value: "flyer", label: "Flyer", desc: "Single-page promotional flyer", icon: FileText },
  { value: "brochure", label: "Brochure", desc: "2-page detailed brochure", icon: FileText },
];

const STYLES = [
  { value: "modern", label: "Modern", desc: "Clean green theme with minimal design", color: "#1A4D2E" },
  { value: "bold", label: "Bold", desc: "Dark navy with orange accents", color: "#0D1B2A" },
  { value: "classic", label: "Classic", desc: "Warm earth tones, traditional feel", color: "#2C3E50" },
];

const FORMATS = [
  { value: "pdf", label: "PDF", desc: "Print-ready document", icon: FileText },
  { value: "png", label: "PNG Image", desc: "High-res image file", icon: Image },
  { value: "pptx", label: "PowerPoint", desc: "Editable presentation", icon: Presentation },
];

export default function FlyerGenerator() {
  const [docType, setDocType] = useState("flyer");
  const [style, setStyle] = useState("modern");
  const [format, setFormat] = useState("pdf");
  const [discountMode, setDiscountMode] = useState("auto");
  const [customCode, setCustomCode] = useState("");
  const [customDesc, setCustomDesc] = useState("");
  const [headline, setHeadline] = useState("");
  const [subheadline, setSubheadline] = useState("");
  const [activeCodes, setActiveCodes] = useState([]);
  const [selectedPromo, setSelectedPromo] = useState("");
  const [history, setHistory] = useState([]);
  const [generating, setGenerating] = useState(false);
  const token = localStorage.getItem("agent_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  useEffect(() => {
    fetchPromoCodes();
    fetchHistory();
  }, []);

  const fetchPromoCodes = async () => {
    try {
      const res = await fetch(`${API}/api/agent/generator/promo-codes/active`, { headers });
      if (res.ok) setActiveCodes(await res.json());
    } catch {}
  };

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API}/api/agent/generator/generations?limit=10`, { headers });
      if (res.ok) setHistory(await res.json());
    } catch {}
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      let discount_code = null;
      let discount_description = null;

      if (discountMode === "custom" && customCode) {
        discount_code = customCode;
        discount_description = customDesc || null;
      } else if (discountMode === "select" && selectedPromo) {
        const code = activeCodes.find(c => c.code === selectedPromo);
        discount_code = code?.code;
        discount_description = code?.description;
      }
      // "auto" mode: backend will auto-pull active promo code

      const body = {
        doc_type: docType,
        template_style: style,
        output_format: format,
        discount_code: discount_code,
        discount_description: discount_description,
        custom_headline: headline || null,
        custom_subheadline: subheadline || null,
      };

      const res = await fetch(`${API}/api/agent/generator/generate`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Generation failed");
      }

      const blob = await res.blob();
      const ext = format === "pptx" ? "pptx" : format === "png" ? "png" : "pdf";
      const filename = `TheRentPayer_${docType}_${style}.${ext}`;

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(`${docType === "flyer" ? "Flyer" : "Brochure"} generated and downloaded!`);
      fetchHistory();
    } catch (e) {
      toast.error("Generation failed: " + e.message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <AgentLayout title="Flyer & Brochure Generator">
      <div className="space-y-6" data-testid="flyer-generator-page">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Flyer & Brochure Generator</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Create professional marketing materials with your branding, contact info, and discount codes.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Configuration Panel */}
          <div className="lg:col-span-2 space-y-5">
            {/* Document Type */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4" /> Document Type
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {DOC_TYPES.map(dt => (
                    <button
                      key={dt.value}
                      onClick={() => setDocType(dt.value)}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        docType === dt.value
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/40"
                      }`}
                      data-testid={`doc-type-${dt.value}`}
                    >
                      <dt.icon className={`h-5 w-5 mb-2 ${docType === dt.value ? "text-primary" : "text-muted-foreground"}`} />
                      <div className="font-semibold text-sm">{dt.label}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{dt.desc}</div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Style Selection */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Palette className="h-4 w-4" /> Template Style
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  {STYLES.map(s => (
                    <button
                      key={s.value}
                      onClick={() => setStyle(s.value)}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        style === s.value
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/40"
                      }`}
                      data-testid={`style-${s.value}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="h-5 w-5 rounded-full border" style={{ backgroundColor: s.color }} />
                        <span className="font-semibold text-sm">{s.label}</span>
                      </div>
                      <div className="text-xs text-muted-foreground">{s.desc}</div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Output Format */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Download className="h-4 w-4" /> Output Format
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  {FORMATS.map(f => (
                    <button
                      key={f.value}
                      onClick={() => setFormat(f.value)}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        format === f.value
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/40"
                      }`}
                      data-testid={`format-${f.value}`}
                    >
                      <f.icon className={`h-5 w-5 mb-2 ${format === f.value ? "text-primary" : "text-muted-foreground"}`} />
                      <div className="font-semibold text-sm">{f.label}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{f.desc}</div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Custom Text */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Custom Text (Optional)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Custom Headline</Label>
                  <Input
                    placeholder="e.g., Never Worry About Rent Again"
                    value={headline}
                    onChange={e => setHeadline(e.target.value)}
                    data-testid="custom-headline"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Custom Subheadline</Label>
                  <Input
                    placeholder="e.g., Affordable coverage starting at just $15/month"
                    value={subheadline}
                    onChange={e => setSubheadline(e.target.value)}
                    data-testid="custom-subheadline"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Discount Code */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Tag className="h-4 w-4" /> Discount Code
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: "auto", label: "Auto-detect" },
                    { value: "select", label: "Choose code" },
                    { value: "custom", label: "Custom code" },
                  ].map(m => (
                    <button
                      key={m.value}
                      onClick={() => setDiscountMode(m.value)}
                      className={`py-2 px-3 rounded-lg border text-sm font-medium transition-all ${
                        discountMode === m.value
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border text-muted-foreground hover:border-primary/40"
                      }`}
                      data-testid={`discount-mode-${m.value}`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>

                {discountMode === "select" && (
                  <Select value={selectedPromo} onValueChange={setSelectedPromo}>
                    <SelectTrigger data-testid="promo-select">
                      <SelectValue placeholder="Select a promo code" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeCodes.map(c => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.code} — {c.description || `${c.discount_value}${c.discount_type === "percent" ? "%" : "$"} off`}
                        </SelectItem>
                      ))}
                      {activeCodes.length === 0 && (
                        <SelectItem value="_none" disabled>No active promo codes</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                )}

                {discountMode === "custom" && (
                  <div className="space-y-2">
                    <Input
                      placeholder="Enter discount code (e.g., SAVE20)"
                      value={customCode}
                      onChange={e => setCustomCode(e.target.value.toUpperCase())}
                      data-testid="custom-discount-code"
                    />
                    <Input
                      placeholder="Discount description (e.g., 20% off your first month)"
                      value={customDesc}
                      onChange={e => setCustomDesc(e.target.value)}
                      data-testid="custom-discount-desc"
                    />
                  </div>
                )}

                {discountMode === "auto" && (
                  <p className="text-xs text-muted-foreground">
                    The generator will automatically include the first active promo code from your settings.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar: Preview & Generate + History */}
          <div className="space-y-5">
            {/* Live Preview Card */}
            <Card className="sticky top-4">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="h-4 w-4" /> Preview & Generate
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Preview summary */}
                <div className="rounded-lg overflow-hidden border">
                  <div className="p-4 text-white text-center" style={{ backgroundColor: STYLES.find(s => s.value === style)?.color }}>
                    <div className="text-lg font-bold">TheRentPayer</div>
                    <div className="text-xs opacity-80 mt-1">
                      {docType === "flyer" ? "Promotional Flyer" : "Marketing Brochure"}
                    </div>
                  </div>
                  <div className="p-4 bg-muted/30 space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Type:</span>
                      <span className="font-medium capitalize">{docType}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Style:</span>
                      <span className="font-medium capitalize">{style}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Format:</span>
                      <span className="font-medium uppercase">{format}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Discount:</span>
                      <span className="font-medium">
                        {discountMode === "custom" ? customCode || "None" : discountMode === "select" ? selectedPromo || "None" : "Auto"}
                      </span>
                    </div>
                    {headline && (
                      <div className="text-xs pt-1 border-t">
                        <span className="text-muted-foreground">Headline: </span>
                        <span className="italic">{headline}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Contact info shown */}
                <div className="bg-muted/50 rounded-lg p-3 text-xs space-y-1">
                  <div className="font-medium text-muted-foreground mb-1">Included Contact Info:</div>
                  <div>(845) 217-4644</div>
                  <div>info@therentpayer.com</div>
                  <div>www.therentpayer.com</div>
                </div>

                <Button
                  className="w-full"
                  size="lg"
                  onClick={handleGenerate}
                  disabled={generating}
                  data-testid="generate-btn"
                >
                  {generating ? (
                    <>
                      <Sparkles className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Generate & Download
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Generation History */}
            {history.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="h-4 w-4" /> Recent Generations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {history.map((gen, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 text-xs">
                        <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                          {gen.output_format === "pptx" ? (
                            <Presentation className="h-4 w-4 text-primary" />
                          ) : gen.output_format === "png" ? (
                            <Image className="h-4 w-4 text-primary" />
                          ) : (
                            <FileText className="h-4 w-4 text-primary" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium capitalize truncate">
                            {gen.doc_type} - {gen.template_style}
                          </div>
                          <div className="text-muted-foreground">
                            {gen.agent_name} &middot; {new Date(gen.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <span className="uppercase text-[10px] font-mono bg-muted px-1.5 py-0.5 rounded">
                          {gen.output_format}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </AgentLayout>
  );
}
